import { Add, Delete, Edit, Search } from "@mui/icons-material";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Link as MuiLink,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, formatUuid } from '../../helpers/formatters';
import { buildProductsSummaryQuery, DEFAULT_PRODUCT_SORT_BY, DEFAULT_PRODUCT_SORT_ORDER, EMPTY_PRODUCT_SUMMARY } from '../../helpers/products';

function ProductsPage() {
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState(DEFAULT_PRODUCT_SORT_BY);
    const [sortOrder, setSortOrder] = useState(DEFAULT_PRODUCT_SORT_ORDER);
    const [summary, setSummary] = useState(EMPTY_PRODUCT_SUMMARY);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [stockModalProduct, setStockModalProduct] = useState(null);
    const [stockDeltaInput, setStockDeltaInput] = useState('');
    const [stockDeltaError, setStockDeltaError] = useState('');
    const [savingStock, setSavingStock] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchProducts = useCallback(() => {
        setIsLoading(true);
        api.get(`products?page=${currentPage}&limit=${itemsPerPage}&q=${activeSearchQuery}&sort_by=${sortBy}&sort_order=${sortOrder}`)
            .then(response => {
                setProducts(response.data.products);
                setTotalPages(response.data.total_pages);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, activeSearchQuery, sortBy, sortOrder]);

    const fetchSummary = useCallback(() => {
        setSummaryLoading(true);
        const summaryQuery = buildProductsSummaryQuery({
            q: activeSearchQuery,
        });
        return api.get(`products/summary?${summaryQuery}`)
            .then((response) => {
                setSummary(response.data || EMPTY_PRODUCT_SUMMARY);
            })
            .catch(() => {
                setSummary(EMPTY_PRODUCT_SUMMARY);
            })
            .finally(() => {
                setSummaryLoading(false);
            });
    }, [activeSearchQuery]);

    useListFetch(fetchProducts, [currentPage, itemsPerPage, activeSearchQuery, sortBy, sortOrder, fetchProducts]);
    useListFetch(fetchSummary, [activeSearchQuery, fetchSummary]);

    const parsedStockDelta = useMemo(() => {
        const trimmed = String(stockDeltaInput || '').trim();
        if (trimmed === '' || trimmed === '-' || trimmed === '+') {
            return null;
        }
        if (!/^-?\d+$/.test(trimmed)) {
            return null;
        }
        return Number(trimmed);
    }, [stockDeltaInput]);

    const previewStock = useMemo(() => {
        if (!stockModalProduct || parsedStockDelta == null) {
            return null;
        }
        return (Number(stockModalProduct.stock_quantity) || 0) + parsedStockDelta;
    }, [stockModalProduct, parsedStockDelta]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder(field === DEFAULT_PRODUCT_SORT_BY ? DEFAULT_PRODUCT_SORT_ORDER : 'asc');
        }
        setCurrentPage(1);
    };

    const resetProductSort = () => {
        setSortBy(DEFAULT_PRODUCT_SORT_BY);
        setSortOrder(DEFAULT_PRODUCT_SORT_ORDER);
    };

    const handleSearch = () => {
        const nextQuery = searchTerm.trim();
        setCurrentPage(1);
        setActiveSearchQuery(nextQuery);
        setAppliedSearchTerm(Boolean(nextQuery));
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
        resetProductSort();
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setActiveSearchQuery('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
        resetProductSort();
    };

    const handleCreateProduct = () => {
        navigate('/admin/register-product');
    };

    const handleEditProduct = (id) => {
        navigate(`/admin/edit-product/${id}`);
    };

    const openStockModal = (product) => {
        setStockModalProduct(product);
        setStockDeltaInput('');
        setStockDeltaError('');
    };

    const closeStockModal = () => {
        if (savingStock) {
            return;
        }
        setStockModalProduct(null);
        setStockDeltaInput('');
        setStockDeltaError('');
    };

    const handleSaveStockAdjustment = () => {
        if (!stockModalProduct) {
            return;
        }
        if (parsedStockDelta == null) {
            setStockDeltaError('Informe um número inteiro (positivo ou negativo).');
            return;
        }
        if (parsedStockDelta === 0) {
            setStockDeltaError('Informe um valor diferente de zero para ajustar o estoque.');
            return;
        }

        setSavingStock(true);
        setStockDeltaError('');
        api.post(`products/${stockModalProduct.id}/stock`, { delta: parsedStockDelta })
            .then((response) => {
                const updated = response.data?.data;
                if (updated) {
                    setProducts((prev) => prev.map((product) => (
                        product.id === stockModalProduct.id
                            ? { ...product, stock_quantity: updated.stock_quantity }
                            : product
                    )));
                }
                setStockModalProduct(null);
                setStockDeltaInput('');
                fetchSummary();
                Swal.fire('Estoque atualizado', 'O ajuste foi aplicado com sucesso.', 'success');
            })
            .catch((error) => {
                const message = error?.response?.data?.error
                    || error?.message
                    || 'Não foi possível ajustar o estoque.';
                setStockDeltaError(message);
            })
            .finally(() => {
                setSavingStock(false);
            });
    };

    const handleDeleteProduct = (productId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Este produto será removido permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed) {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`products/${productId}`)
                        .then(() => {
                            setProducts(products.filter(product => product.id !== productId));
                            Swal.fire('Removido!', 'O produto foi removido com sucesso.', 'success');
                            setLoadingDelete(false);
                            fetchSummary();
                        })
                        .catch(() => {
                            Swal.fire('Erro!', 'Houve um problema ao remover o produto.', 'error');
                            setLoadingDelete(false);
                        });
                }
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Produtos</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                <Typography variant="h4" gutterBottom>
                    Produtos
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar produto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateProduct}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Produto
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total de produtos
                            </Typography>
                            <Typography variant="h4">
                                {summaryLoading ? <CircularProgress size={28} /> : (summary.total_products ?? 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Preços de venda
                            </Typography>
                            {summaryLoading ? (
                                <CircularProgress size={28} />
                            ) : (
                                <>
                                    <Typography variant="body2">
                                        Menor: {formatCurrencyInCents(summary.min_price ?? 0)}
                                    </Typography>
                                    <Typography variant="body2">
                                        Maior: {formatCurrencyInCents(summary.max_price ?? 0)}
                                    </Typography>
                                    <Typography variant="body2">
                                        Média: {formatCurrencyInCents(summary.average_price ?? 0)}
                                    </Typography>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total vendido
                            </Typography>
                            <Typography variant="h4">
                                {summaryLoading ? <CircularProgress size={28} /> : (summary.total_sales_quantity ?? 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Estoque atual
                            </Typography>
                            <Typography variant="h4">
                                {summaryLoading ? <CircularProgress size={28} /> : (summary.total_stock_quantity ?? 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'name'}
                                    direction={sortBy === 'name' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('name')}
                                >
                                    Nome
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Preço de Venda Sugerido</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'stock_quantity'}
                                    direction={sortBy === 'stock_quantity' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('stock_quantity')}
                                >
                                    Estoque
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'total_sales_quantity'}
                                    direction={sortBy === 'total_sales_quantity' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('total_sales_quantity')}
                                >
                                    Qtd. vendida
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === DEFAULT_PRODUCT_SORT_BY}
                                    direction={sortBy === DEFAULT_PRODUCT_SORT_BY ? sortOrder : DEFAULT_PRODUCT_SORT_ORDER}
                                    onClick={() => handleSort(DEFAULT_PRODUCT_SORT_BY)}
                                >
                                    Data de Criação
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(products) && products.length > 0 && products.map((product) => (
                            <TableRow key={product.id}>
                                <TableCell>{formatUuid(product.id)}</TableCell>
                                <TableCell>{product.name}</TableCell>
                                <TableCell>{formatCurrencyInCents(product.base_price)}</TableCell>
                                <TableCell>
                                    <MuiLink
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        onClick={() => openStockModal(product)}
                                        sx={{ font: 'inherit', cursor: 'pointer' }}
                                    >
                                        {product.stock_quantity}
                                    </MuiLink>
                                </TableCell>
                                <TableCell>{product.total_sales_quantity ?? 0}</TableCell>
                                <TableCell>{formatDate(product.created_at)}</TableCell>
                                <TableCell>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditProduct(product.id)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteProduct(product.id)}
                                        aria-label="remover produto"
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mt: 3,
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>

            <Dialog open={Boolean(stockModalProduct)} onClose={closeStockModal} fullWidth maxWidth="sm">
                <DialogTitle>Ajustar estoque</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Esta ação é um <strong>incremento ou decremento</strong> do estoque atual.
                        O valor informado será <strong>somado</strong> (se positivo) ou <strong>subtraído</strong> (se negativo)
                        do estoque atual — não informe o total desejado aqui.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Produto: <strong>{stockModalProduct?.name}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Estoque atual: <strong>{stockModalProduct?.stock_quantity ?? 0}</strong>
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Ajuste de quantidade"
                        placeholder="Ex.: 5 para entrar, -3 para sair"
                        value={stockDeltaInput}
                        onChange={(e) => {
                            setStockDeltaInput(e.target.value);
                            setStockDeltaError('');
                        }}
                        error={Boolean(stockDeltaError)}
                        helperText={stockDeltaError || 'Use número positivo para aumentar e negativo para diminuir.'}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                    {previewStock != null && (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Estoque após o ajuste: <strong>{previewStock}</strong>
                            {' '}
                            ({(Number(stockModalProduct?.stock_quantity) || 0)} {parsedStockDelta >= 0 ? '+' : ''}{parsedStockDelta})
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeStockModal} disabled={savingStock}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveStockAdjustment}
                        disabled={savingStock}
                        startIcon={savingStock ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {savingStock ? 'Salvando...' : 'Salvar ajuste'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ProductsPage;
